import { useEffect, useRef, useState } from "react";
import { Link } from "@tanstack/react-router";
import { AlertTriangle, CalendarClock, X } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";
import type { AppointmentAttention } from "../pages/AgendaPage";
import "../styles/agenda-reminders.css";

type Reminder = {
  key: string;
  title: string;
  date: string;
  dueTime: string;
  kind: "overdue" | "soon";
};

function dueTime(startMinute: number, durationMinutes: number) {
  const end = startMinute + durationMinutes;
  return `${String(Math.floor(end / 60)).padStart(2, "0")}:${String(end % 60).padStart(2, "0")}`;
}

export function AgendaReminderAlerts({ userId }: { userId?: string }) {
  const seen = useRef(new Set<string>());
  const queue = useRef<Reminder[]>([]);
  const [active, setActive] = useState<Reminder | null>(null);
  const attention = useQuery({
    queryKey: ["appointments", "attention", "mine", userId],
    queryFn: () => api<AppointmentAttention>("/v1/appointments/attention?scope=mine&mode=alerts"),
    enabled: Boolean(userId),
    refetchInterval: 30_000,
    refetchOnWindowFocus: true
  });

  useEffect(() => {
    if (!attention.data) return;
    const candidates = [
      ...attention.data.overdue.map((appointment) => ({ appointment, kind: "overdue" as const })),
      ...attention.data.dueSoon.map((appointment) => ({ appointment, kind: "soon" as const }))
    ];
    const currentKeys = new Set(candidates.map(({ appointment, kind }) =>
      [appointment.id, appointment.date, appointment.startMinute, appointment.durationMinutes, kind].join(":")));
    queue.current = queue.current.filter((reminder) => currentKeys.has(reminder.key));
    for (const key of seen.current) if (!currentKeys.has(key)) seen.current.delete(key);
    for (const { appointment, kind } of candidates) {
      const key = [appointment.id, appointment.date, appointment.startMinute, appointment.durationMinutes, kind].join(":");
      if (seen.current.has(key)) continue;
      seen.current.add(key);
      queue.current.push({
        key,
        title: appointment.title,
        date: appointment.date,
        dueTime: dueTime(appointment.startMinute, appointment.durationMinutes),
        kind
      });
    }
    setActive((current) => current && currentKeys.has(current.key) ? current : queue.current.shift() || null);
  }, [attention.data]);

  useEffect(() => {
    if (!active) return;
    const timeout = window.setTimeout(() => setActive(queue.current.shift() || null), 7_000);
    return () => window.clearTimeout(timeout);
  }, [active]);

  if (!active) return null;
  return (
    <div className={`agenda-reminder-popup ${active.kind}`} role="status" aria-live="polite">
      <span className="agenda-reminder-symbol" aria-hidden="true">
        {active.kind === "overdue" ? <AlertTriangle size={19} /> : <CalendarClock size={19} />}
      </span>
      <div className="agenda-reminder-content">
        <strong>{active.kind === "overdue" ? "Tarefa atrasada" : "Vence em breve"}</strong>
        <span>{active.title}</span>
        <small>{active.kind === "overdue" ? "Prazo encerrado" : "Prazo previsto"} às {active.dueTime}</small>
        <Link
          to="/agenda"
          search={active.kind === "overdue" ? { view: "overdue" } : { date: active.date }}
          onClick={() => setActive(queue.current.shift() || null)}
        >Ver na agenda</Link>
      </div>
      <button type="button" aria-label="Dispensar lembrete" onClick={() => setActive(queue.current.shift() || null)}><X size={15} /></button>
    </div>
  );
}
